// src/pages/Error/ErrorPage.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaExclamationTriangle,
  FaHome,
  FaRedo,
  FaShieldAlt,
  FaSignInAlt,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

export default function ErrorPage() {
  useScrollToTop();

  const location = useLocation();
  const navigate = useNavigate();

  const errorMessage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawError = params.get("error");

    if (!rawError) {
      return "Une erreur inconnue est survenue.";
    }

    return decodeURIComponent(rawError);
  }, [location.search]);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  const reloadPage = () => {
    window.location.reload();
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center px-4 py-10 pt-[120px] text-slate-900 dark:text-white sm:px-6 md:pt-[145px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <ScrollIndicator />

      <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-[2px]" />
      <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-red-500/25 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-indigo-500/25 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-500/10 blur-3xl" />

      <div className="relative flex min-h-[calc(100vh-170px)] items-center justify-center">
        <section className="w-full max-w-3xl overflow-hidden rounded-[36px] border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
          <div className="relative overflow-hidden bg-slate-950 px-6 py-9 text-center text-white sm:px-8 sm:py-12">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-red-500/25 blur-3xl" />
            <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-indigo-500/25 blur-3xl" />

            <div className="relative">
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-[28px] border border-white/15 bg-white/10 text-3xl shadow-lg backdrop-blur">
                <FaExclamationTriangle />
              </div>

              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                <FaShieldAlt />
                Une erreur est survenue
              </div>

              <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">
                Oups, quelque chose bloque
              </h1>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                La page demandée n’a pas pu être affichée correctement. Vous
                pouvez revenir en arrière, retourner à l’accueil ou réessayer.
              </p>
            </div>
          </div>

          <div className="p-5 sm:p-7">
            <div className="rounded-[26px] border border-red-200 bg-red-50 p-5 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />

                <div>
                  <h2 className="font-bold">Détail de l’erreur</h2>
                  <p className="mt-1 text-sm leading-relaxed">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-300">
              Si le problème persiste, essayez de vous reconnecter ou contactez
              l’organisateur / administrateur de l’application.
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <FaArrowLeft />
                Retour
              </button>

              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaHome />
                Accueil
              </button>

              <button
                type="button"
                onClick={reloadPage}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-700 transition hover:-translate-y-0.5 hover:bg-indigo-100 dark:bg-indigo-950/70 dark:text-indigo-300 dark:hover:bg-indigo-950"
              >
                <FaRedo />
                Réessayer
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-pink-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                <FaSignInAlt />
                Connexion
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}